import CoreBase from '../../../services/engines/CoreBase';
import documentService from '../../../services/DocumentService';
import attachmentEngine from './AttachmentEngine';
import responseHelper from '../../../services/ResponseHelper';

let _name = 'LightBoxHandler';

/**
 * LightBoxHandler — handles all LightBox actions.
 * Extends CoreBase for token access and error logging.
 * Singleton — one instance shared across the app.
 */
class LightBoxHandler extends CoreBase {
    /**
     * Navigates to the previous image.
     * @param {Function} setIndex
     */
    handlePrev = (setIndex) => {
        setIndex((i) => Math.max(0, i - 1));
    };

    /**
     * Navigates to the next image.
     * @param {Function} setIndex
     * @param {number} total
     */
    handleNext = (setIndex, total) => {
        setIndex((i) => Math.min(total - 1, i + 1));
    };

    /**
     * Zoom in/out via scroll wheel.
     * @param {WheelEvent} e
     * @param {Function} setScale
     */
    handleWheel = (e, setScale) => {
        e.preventDefault();
        setScale((s) => Math.min(4, Math.max(0.5, s + (e.deltaY < 0 ? 0.1 : -0.1))));
    };

    /**
     * Rotates image 90 degrees clockwise.
     * Updates local state immediately — patches BE fire-and-forget.
     * @param {object} doc - current document entity
     * @param {number} rotation - current rotation in degrees
     * @param {Function} setRotation
     */
    handleRotateCW = async (doc, rotation, setRotation) => {
        try {
            const next = (rotation + 90) % 360;
            setRotation(next);
            // Keep doc in sync for when lightbox closes and strip re-renders
            doc.rotation = next;
            // Fire and forget — BE not built yet, 404 is acceptable
            documentService.patchRotation(doc.rk, next).catch(() => {});
        } catch (error) {
            this.logError('handleRotateCW', error.message);
        }
    };

    /**
     * Rotates image 90 degrees counter-clockwise.
     * Updates local state immediately — patches BE fire-and-forget.
     * @param {object} doc - current document entity
     * @param {number} rotation - current rotation in degrees
     * @param {Function} setRotation
     */
    handleRotateCCW = async (doc, rotation, setRotation) => {
        try {
            const next = (rotation + 270) % 360;
            setRotation(next);
            doc.rotation = next;
            // Fire and forget — BE not built yet, 404 is acceptable
            documentService.patchRotation(doc.rk, next).catch(() => {});
        } catch (error) {
            this.logError('handleRotateCCW', error.message);
        }
    };

    /**
     * Downloads current image via anchor tag.
     * @param {object} doc - current document entity
     */
    handleDownload = (doc) => {
        try {
            const a = document.createElement('a');
            a.href = doc.url;
            a.download = doc.fileName;
            a.click();
        } catch (error) {
            this.logError('handleDownload', error.message);
        }
    };

    /**
     * Deletes current image after confirm.
     * Removes from AttachmentEngine cache (strip updates), removes from local
     * lightbox documents array, and closes lightbox if it was the last image.
     * @param {object} doc - current document entity
     * @param {object[]} documents - current lightbox documents
     * @param {number} index - current index
     * @param {Function} setDocuments
     * @param {Function} setIndex
     * @param {Function} onClose
     */
    handleDelete = async (doc, documents, index, setDocuments, setIndex, onClose) => {
        try {
            // rk = document id aka the image
            // rid = root id aka the journal
            const dto = { rk: doc.rk, rid: doc.rid };
            const resp = await documentService.deleteDocument(dto);
            if (responseHelper.check(resp, '', `${_name}.handleDelete`) < 0) {
                //  this.setState({ items: [] });
                return;
            }

            // Strip + cache in sync
            attachmentEngine.removeDocument(doc.rk);

            const remaining = documents.filter((d) => d.rk !== doc.rk);

            if (remaining.length === 0) {
                onClose?.();
                return;
            }

            setDocuments(remaining);
            setIndex(Math.min(index, remaining.length - 1));
        } catch (error) {
            this.logError('handleDelete', error.message);
        }
    };

    /**
     * Closes lightbox when clicking the overlay backdrop.
     * @param {MouseEvent} e
     * @param {Function} onClose
     */
    handleBackdropClick = (e, onClose) => {
        if (e.target === e.currentTarget) onClose?.();
    };

    /**
     * Handles keyboard navigation — Escape, ArrowLeft, ArrowRight.
     * @param {KeyboardEvent} e
     * @param {Function} setIndex
     * @param {number} total
     * @param {Function} onClose
     */
    handleKey = (e, setIndex, total, onClose) => {
        if (e.key === 'Escape') onClose?.();
        if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
        if (e.key === 'ArrowRight') setIndex((i) => Math.min(total - 1, i + 1));
    };
}

const lightBoxHandler = new LightBoxHandler();
export default lightBoxHandler;
